import type {
  ApiError,
  Merchant,
  MerchantDetails,
  MerchantVerification,
  PartnerTypes,
} from '../types';
import { isMerchantArray, isPartnerTypes } from '../guards';
import { BASE_URL, buildQueryParams, fetchWithAuth } from './http';

export const fetchPartnerDetails = async (): Promise<PartnerTypes> => {
  const fallback: PartnerTypes = {
    id: '',
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    category: '',
    city: '',
    country: '',
    locale: '',
    currency: '',
    website: '',
    registrationNumber: '',
    createdAt: '',
    updatedAt: '',
  };
  try {
    const url = `${BASE_URL}/onboarding/v1/partners/me`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: {} });
    if (!isPartnerTypes(response)) {
      console.warn('fetchPartnerDetails: unexpected response shape', response);
      return fallback;
    }
    return response;
  } catch (error) {
    console.error('fetchPartnerDetails failed:', error);
    return fallback;
  }
};

export const fetchMerchants = async (
  searchQuery: string | boolean
): Promise<Merchant[]> => {
  try {
    const queryParams = buildQueryParams({ name: searchQuery || '' });
    const url = `${BASE_URL}/onboarding/v1/merchants${
      searchQuery ? '/search?' : ''
    }${queryParams}`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: {} });
    if (!isMerchantArray(response)) {
      console.warn('fetchMerchants: unexpected response shape', response);
      return [];
    }
    return response;
  } catch (error) {
    console.error('fetchMerchants failed:', error);
    return [];
  }
};

export const fetchRepresentative = async (
  id: string
): Promise<MerchantDetails | object> => {
  try {
    const queryParams = buildQueryParams({ merchant_id: id });
    const url = `${BASE_URL}/onboarding/v1/representatives/search?${queryParams}`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: {} });
    return response as MerchantDetails;
  } catch (error) {
    console.error('fetchRepresentative failed:', error);
    return {} as MerchantDetails;
  }
};

export const fetchDocStatus = async (
  merchantId: string
): Promise<MerchantVerification | null> => {
  try {
    const queryParams = buildQueryParams({ merchant_id: merchantId });
    const url = `${BASE_URL}/onboarding/v1/documents/status?${queryParams}`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: {} });
    return response as MerchantVerification | null;
  } catch (error) {
    console.error('fetchDocStatus failed:', error);
    return null;
  }
};

export const updateDocStatus = async (
  payload: MerchantVerification
): Promise<MerchantVerification | null> => {
  try {
    const apiPayload = {
      identityDocumentVerified: payload.identityDocumentVerified,
      companyLicenseVerified: payload.companyLicenseVerified,
      companyLicense2Verified: payload.companyLicense2Verified,
    };
    const url = `${BASE_URL}/onboarding/v1/documents/status/${payload?.id}`;
    const response = await fetchWithAuth(
      url,
      { method: 'PUT', headers: {} },
      {},
      apiPayload
    );
    return response as MerchantVerification | null;
  } catch (error) {
    console.error('updateDocStatus failed:', error);
    return null;
  }
};

export const fetchConnection = async (id: string): Promise<string | null> => {
  try {
    const url = `${BASE_URL}/onboarding/v1/merchants/${id}/connect`;
    const response = await fetchWithAuth(url, { method: 'POST', headers: {} });
    return response as string | null;
  } catch (error) {
    console.error('fetchConnection failed:', error);
    return null;
  }
};

export const createMerchants = async (
  paylaod: unknown
): Promise<Merchant | ApiError> => {
  try {
    const url = `${BASE_URL}/onboarding/v1/merchants?skip_otp=true`;
    const response = await fetchWithAuth(
      url,
      { method: 'POST', headers: {} },
      {},
      paylaod
    );
    return response as Merchant;
  } catch (error) {
    console.error('createMerchants failed:', error);
    return error as ApiError;
  }
};
